import { useState, useEffect } from 'react';
import { tokenStorage } from '@/services/tokenStorage';
import { refreshAccessToken } from '@/services/tokenRefresh';

interface TokenValidationResult {
  isValidating: boolean;
  hasValidTokens: boolean;
  token?: string;
  refreshToken?: string;
  shouldUseMsal: boolean;
  error?: string;
}

export function useStoredTokenValidation(): TokenValidationResult {
  const [result, setResult] = useState<TokenValidationResult>({
    isValidating: true,
    hasValidTokens: false,
    shouldUseMsal: false,
  });

  useEffect(() => {
    let mounted = true;

    async function validateStoredTokens() {
      try {
        console.log('[Auth] Checking for stored tokens...');

        // Check for stored tokens
        const storedTokens = tokenStorage.getTokens();

        if (!storedTokens) {
          console.log('[Auth] No stored tokens found, will use MSAL');
          if (mounted) {
            setResult({
              isValidating: false,
              hasValidTokens: false,
              shouldUseMsal: true,
            });
          }
          return;
        }

        console.log('[Auth] Found stored tokens, validating...');

        // Check if token needs refresh (expires in less than 5 minutes)
        const timeUntilExpiry = tokenStorage.getTimeUntilExpiry();
        console.log(
          `[Auth] Token expires in ${Math.floor(timeUntilExpiry / 60)} minutes`,
        );

        if (timeUntilExpiry < 300) {
          // Less than 5 minutes
          console.log('[Auth] Token near expiry, attempting refresh...');

          try {
            const response = await refreshAccessToken(
              storedTokens.refreshToken,
            );

            // Save new tokens
            tokenStorage.saveTokens(response.token, response.refreshToken);
            console.log('[Auth] Token refresh successful');

            if (mounted) {
              setResult({
                isValidating: false,
                hasValidTokens: true,
                token: response.token,
                refreshToken: response.refreshToken,
                shouldUseMsal: false,
              });
            }
          } catch (refreshError) {
            console.error('[Auth] Token refresh failed:', refreshError);
            tokenStorage.clearTokens();

            if (mounted) {
              setResult({
                isValidating: false,
                hasValidTokens: false,
                shouldUseMsal: true,
                error: 'Token refresh failed',
              });
            }
          }
        } else {
          // Tokens are still valid
          console.log('[Auth] Stored tokens are valid');

          if (mounted) {
            setResult({
              isValidating: false,
              hasValidTokens: true,
              token: storedTokens.token,
              refreshToken: storedTokens.refreshToken,
              shouldUseMsal: false,
            });
          }
        }
      } catch (error) {
        console.error('[Auth] Token validation error:', error);
        tokenStorage.clearTokens();

        if (mounted) {
          setResult({
            isValidating: false,
            hasValidTokens: false,
            shouldUseMsal: true,
            error: (error as Error).message,
          });
        }
      }
    }

    validateStoredTokens();

    return () => {
      mounted = false;
    };
  }, []);

  return result;
}
